import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Plus, FileText, Mail, Download, ArrowRight, Search,
  Pencil, Trash2, CheckCircle, XCircle, ChevronDown, ChevronUp,
  User, Building, Phone, MapPin, Package, Euro, Calendar, Clock, ReceiptText
} from 'lucide-react';
import { PackName } from '../../ui/PackName';
import DeleteConfirmModal from '../DeleteConfirmModal';

const API = window.location.origin + '/api';

const STATUS_MAP = {
  draft: { label: 'Brouillon', color: 'bg-gray-500/20 text-gray-400' },
  sent: { label: 'En attente', color: 'bg-amber-500/20 text-amber-400' },
  accepted: { label: 'Accepté', color: 'bg-green-500/20 text-green-400' },
  rejected: { label: 'Refusé', color: 'bg-red-500/20 text-red-400' },
  expired: { label: 'Expiré', color: 'bg-yellow-500/20 text-yellow-400' },
  converted: { label: 'Converti', color: 'bg-purple-500/20 text-purple-400' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const QuotesView = ({ packOptions = [] }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [deleteModal, setDeleteModal] = useState({ open: false, description: '', action: null });

  // Form state
  const [form, setForm] = useState({
    client_firstName: '', client_lastName: '', client_company: '',
    client_email: '', client_phone: '',
    client_address: '', client_postalCode: '', client_city: '',
    customer_type: 'contact',
    selected_pack_id: '', pack_name: '', pack_surface: '',
    pitch: '',
    monthly_price: '', caution: '', transport_fees: '0',
    discount_type: 'percentage', discount_value: '0',
    duration_months: '1', start_date: '',
    validity_days: '30', notes: '', payment_conditions: 'Paiement par prélèvement mensuel automatique',
    commercial_name: '',
  });

  const resetForm = () => {
    setForm({
      client_firstName: '', client_lastName: '', client_company: '',
      client_email: '', client_phone: '',
      client_address: '', client_postalCode: '', client_city: '',
      customer_type: 'contact',
      selected_pack_id: '', pack_name: '', pack_surface: '',
      pitch: '',
      monthly_price: '', caution: '', transport_fees: '0',
      discount_type: 'percentage', discount_value: '0',
      duration_months: '1', start_date: '',
      validity_days: '30', notes: '', payment_conditions: 'Paiement par prélèvement mensuel automatique',
      commercial_name: '',
    });
    setEditingQuote(null);
  };

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quotes`, { withCredentials: true });
      setQuotes(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handlePackChange = (packId) => {
    // Si on reclique sur le pack déjà sélectionné, on désélectionne
    if (form.selected_pack_id === packId) {
      setForm(prev => ({
        ...prev,
        selected_pack_id: '',
        pack_name: '',
        pack_surface: '',
        monthly_price: '',
        caution: '',
      }));
      return;
    }
    const pack = packOptions.find(p => p.id === packId);
    if (pack) {
      setForm(prev => ({
        ...prev,
        selected_pack_id: packId,
        pack_name: pack.name,
        pack_surface: pack.surface,
        monthly_price: String(pack.price),
        caution: String(pack.caution || pack.price),
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.client_email || !form.pack_name) {
      toast.error('Veuillez remplir au minimum l\'email client et le pack');
      return;
    }
    setActionLoading(prev => ({ ...prev, submit: true }));
    try {
      const payload = { ...form };
      if (editingQuote) {
        await axios.put(`${API}/admin/quotes/${editingQuote.id}`, payload, { withCredentials: true });
        toast.success('Devis mis à jour');
      } else {
        await axios.post(`${API}/admin/quotes`, payload, { withCredentials: true });
        toast.success('Devis créé avec succès');
      }
      setShowForm(false);
      resetForm();
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setActionLoading(prev => ({ ...prev, submit: false }));
    }
  };

  const handleEdit = (quote) => {
    setForm({
      client_firstName: quote.client?.firstName || '',
      client_lastName: quote.client?.lastName || '',
      client_company: quote.client?.company || '',
      client_email: quote.client?.email || '',
      client_phone: quote.client?.phone || '',
      client_address: quote.client?.address || '',
      client_postalCode: quote.client?.postalCode || '',
      client_city: quote.client?.city || '',
      selected_pack_id: quote.selected_pack_id || '',
      pack_name: quote.pack_name || '',
      pack_surface: quote.pack_surface || '',
      pitch: quote.pitch || '',
      monthly_price: String(quote.monthly_price || ''),
      caution: String(quote.caution || ''),
      transport_fees: String(quote.transport_fees || '0'),
      discount_type: quote.discount_type || 'percentage',
      discount_value: String(quote.discount_value || '0'),
      duration_months: String(quote.duration_months || '1'),
      start_date: quote.start_date ? quote.start_date.split('T')[0] : '',
      validity_days: String(quote.validity_days || '30'),
      notes: quote.notes || '',
      payment_conditions: quote.payment_conditions || '',
      commercial_name: quote.commercial_name || '',
    });
    setEditingQuote(quote);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setDeleteModal({
      open: true,
      description: 'Supprimer ce devis ? Cette action est irréversible.',
      action: async () => {
        try {
          await axios.delete(`${API}/admin/quotes/${id}`, { withCredentials: true });
          toast.success('Devis supprimé');
          fetchQuotes();
        } catch (err) {
          toast.error('Erreur lors de la suppression');
        }
      }
    });
  };

  const handleDownloadPdf = async (id, quoteNumber) => {
    setActionLoading(prev => ({ ...prev, [id + '_pdf']: true }));
    try {
      const res = await axios.get(`${API}/admin/quotes/${id}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${quoteNumber.replace(/-/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setActionLoading(prev => ({ ...prev, [id + '_pdf']: false }));
    }
  };

  const handleSendEmail = async (id) => {
    setActionLoading(prev => ({ ...prev, [id + '_email']: true }));
    try {
      await axios.post(`${API}/admin/quotes/${id}/send-email`, {}, { withCredentials: true });
      toast.success('Devis envoyé par email');
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setActionLoading(prev => ({ ...prev, [id + '_email']: false }));
    }
  };

  const handleMarkAccepted = async (id) => {
    setActionLoading(prev => ({ ...prev, [id + '_accept']: true }));
    try {
      await axios.post(`${API}/admin/quotes/${id}/mark-accepted`, {}, { withCredentials: true });
      toast.success('Devis marqué comme accepté');
      fetchQuotes();
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setActionLoading(prev => ({ ...prev, [id + '_accept']: false }));
    }
  };

  const handleMarkRejected = async (id) => {
    try {
      await axios.post(`${API}/admin/quotes/${id}/mark-rejected`, {}, { withCredentials: true });
      toast.success('Devis marqué comme refusé');
      fetchQuotes();
    } catch { toast.error('Erreur'); }
  };

  const handleConvert = async (id) => {
    if (!window.confirm('Convertir ce devis en commande manuelle ?')) return;
    setActionLoading(prev => ({ ...prev, [id + '_convert']: true }));
    try {
      await axios.post(`${API}/admin/quotes/${id}/convert`, {}, { withCredentials: true });
      toast.success('Devis converti en commande manuelle');
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setActionLoading(prev => ({ ...prev, [id + '_convert']: false }));
    }
  };

  // Computed totals for preview (input is TTC, on extrait HT/TVA)
  const previewTotals = () => {
    const mp = parseFloat(form.monthly_price) || 0;  // TTC
    const tf = parseFloat(form.transport_fees) || 0; // TTC
    const dv = parseFloat(form.discount_value) || 0;
    const da = form.discount_type === 'percentage' ? mp * dv / 100 : dv;
    const fmTtc = Math.max(0, mp - da);
    const ttc = fmTtc + tf;
    const ht = Math.round((ttc / 1.20) * 100) / 100;
    const tva = Math.round((ttc - ht) * 100) / 100;
    return {
      discount: da.toFixed(2),
      finalMonthly: fmTtc.toFixed(2),
      ht: ht.toFixed(2),
      tva: tva.toFixed(2),
      ttc: ttc.toFixed(2),
    };
  };

  const filteredQuotes = quotes.filter(q => {
    if (!searchQuery) return true;
    const s = searchQuery.toLowerCase();
    return (
      q.quote_number?.toLowerCase().includes(s) ||
      q.client?.company?.toLowerCase().includes(s) ||
      q.client?.lastName?.toLowerCase().includes(s) ||
      q.client?.email?.toLowerCase().includes(s) ||
      q.pack_name?.toLowerCase().includes(s)
    );
  });

  const totals = previewTotals();

  // ─── FORM ────────────────────────────
  if (showForm) {
    return (
      <div data-testid="quote-form" className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {editingQuote ? `Modifier le devis ${editingQuote.quote_number}` : 'Nouveau devis'}
          </h2>
          <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}
            className="border-slate-600 text-slate-300 hover:bg-slate-700">
            Annuler
          </Button>
        </div>

        {/* Client Info */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <User className="w-4 h-4" /> Informations client
          </h3>

          {/* Status selector: Client or Contact */}
          <div className="mb-4">
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Statut</label>
            <div className="flex gap-2" role="radiogroup">
              {[
                { id: 'contact', label: 'Contact (prospect)', desc: 'N\'a pas encore loué' },
                { id: 'client', label: 'Client', desc: 'A déjà une location ou paiement' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, customer_type: opt.id }))}
                  data-testid={`quote-customer-type-${opt.id}`}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-all ${
                    form.customer_type === opt.id
                      ? 'border-[#DFFF00] bg-[#DFFF00]/10 text-white'
                      : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  <div className="font-medium text-sm">{opt.label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input placeholder="Société" value={form.client_company}
              onChange={e => setForm(p => ({ ...p, client_company: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" data-testid="quote-client-company" />
            <Input placeholder="Prénom" value={form.client_firstName}
              onChange={e => setForm(p => ({ ...p, client_firstName: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" data-testid="quote-client-firstname" />
            <Input placeholder="Nom" value={form.client_lastName}
              onChange={e => setForm(p => ({ ...p, client_lastName: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" data-testid="quote-client-lastname" />
            <Input placeholder="Email" type="email" value={form.client_email}
              onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" data-testid="quote-client-email" />
            <Input placeholder="Téléphone" value={form.client_phone}
              onChange={e => setForm(p => ({ ...p, client_phone: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" data-testid="quote-client-phone" />
            <Input placeholder="Adresse" value={form.client_address}
              onChange={e => setForm(p => ({ ...p, client_address: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" />
            <Input placeholder="Code postal" value={form.client_postalCode}
              onChange={e => setForm(p => ({ ...p, client_postalCode: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" />
            <Input placeholder="Ville" value={form.client_city}
              onChange={e => setForm(p => ({ ...p, client_city: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" />
          </div>
        </div>

        {/* Pack & Pricing */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Package className="w-4 h-4" /> Pack & Tarification
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {packOptions.map(pack => (
              <button key={pack.id}
                onClick={() => handlePackChange(pack.id)}
                data-testid={`quote-select-pack-${pack.id}`}
                className={`p-3 rounded-lg border text-left transition-all ${
                  form.selected_pack_id === pack.id
                    ? 'border-[#DFFF00] bg-[#DFFF00]/10 text-white'
                    : 'border-slate-600 bg-slate-900 text-slate-400 hover:border-slate-500'
                }`}>
                <div className="font-semibold text-sm"><PackName name={pack.name} /></div>
                <div className="text-xs opacity-70">{pack.surface}</div>
                <div className="text-[#DFFF00] font-bold mt-1">{pack.price}€/mois</div>
              </button>
            ))}
          </div>

          {/* Custom pack name + surface — editable, used when no catalogue pack matches */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 p-3 bg-slate-900/40 border border-slate-700/60 rounded-lg">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500 mb-1 block flex items-center gap-2">
                Nom du pack <span className="text-[10px] text-slate-600">(éditable — laissez vide ou personnalisez)</span>
              </label>
              <Input
                type="text"
                value={form.pack_name}
                onChange={e => setForm(p => ({ ...p, pack_name: e.target.value, selected_pack_id: '' }))}
                placeholder="Ex: Pack 20m² LED Outdoor sur-mesure"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="quote-pack-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Surface / dimensions</label>
              <Input
                type="text"
                value={form.pack_surface}
                onChange={e => setForm(p => ({ ...p, pack_surface: e.target.value }))}
                placeholder="Ex: 20m²"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="quote-pack-surface-input"
              />
            </div>
          </div>

          {/* Choix du pitch (optionnel) */}
          <div className="mb-4 p-3 bg-slate-900/60 rounded-lg border border-slate-700/60">
            <label className="text-xs text-slate-500 mb-1 block flex items-center gap-2">
              Choix du pitch <span className="text-[10px] text-slate-600 font-normal">(optionnel)</span>
            </label>
            <Input
              type="text"
              placeholder="Ex : P2.6 outdoor, P3.91, P4.81..."
              value={form.pitch}
              onChange={e => setForm(p => ({ ...p, pitch: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white"
              data-testid="quote-pitch"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Cette mention apparaîtra sur le devis et sur le contrat de location.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Prix mensuel TTC</label>
              <Input type="number" value={form.monthly_price}
                onChange={e => setForm(p => ({ ...p, monthly_price: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" data-testid="quote-monthly-price" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Caution</label>
              <Input type="number" value={form.caution}
                onChange={e => setForm(p => ({ ...p, caution: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" data-testid="quote-caution" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Frais transport TTC</label>
              <Input type="number" value={form.transport_fees}
                onChange={e => setForm(p => ({ ...p, transport_fees: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Remise</label>
              <div className="flex gap-2">
                <select value={form.discount_type}
                  onChange={e => setForm(p => ({ ...p, discount_type: e.target.value }))}
                  className="bg-slate-900 border border-slate-600 rounded-md text-white text-sm px-2">
                  <option value="percentage">%</option>
                  <option value="fixed">€</option>
                </select>
                <Input type="number" value={form.discount_value}
                  onChange={e => setForm(p => ({ ...p, discount_value: e.target.value }))}
                  className="bg-slate-900 border-slate-600 text-white" />
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="mt-4 p-3 bg-slate-900 rounded-lg border border-slate-700 grid grid-cols-5 gap-3 text-center text-sm">
            <div>
              <div className="text-slate-500 text-xs">Remise</div>
              <div className="text-red-400 font-semibold">-{totals.discount}€</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Mensuel TTC</div>
              <div className="text-white font-semibold">{totals.finalMonthly}€</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Total HT</div>
              <div className="text-white font-semibold">{totals.ht}€</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">TVA (20%)</div>
              <div className="text-white font-semibold">{totals.tva}€</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Total TTC</div>
              <div className="text-[#DFFF00] font-bold text-lg">{totals.ttc}€</div>
            </div>
          </div>
        </div>

        {/* Duration & Dates */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Durée & Dates
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Durée indicative (mois)</label>
              <Input type="number" min="1" value={form.duration_months}
                onChange={e => setForm(p => ({ ...p, duration_months: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Date de début souhaitée</label>
              <Input type="date" value={form.start_date}
                onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" data-testid="quote-start-date" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Validité du devis (jours)</label>
              <Input type="number" value={form.validity_days}
                onChange={e => setForm(p => ({ ...p, validity_days: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Nom du commercial</label>
              <Input value={form.commercial_name}
                onChange={e => setForm(p => ({ ...p, commercial_name: e.target.value }))}
                className="bg-slate-900 border-slate-600 text-white" data-testid="quote-commercial-name" />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Notes & Conditions
          </h3>
          <textarea rows={3} value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            placeholder="Notes internes ou conditions particulières..."
            className="w-full bg-slate-900 border border-slate-600 rounded-lg text-white p-3 text-sm resize-none focus:ring-1 focus:ring-[#DFFF00] focus:border-[#DFFF00]" />
          <div className="mt-3">
            <label className="text-xs text-slate-500 mb-1 block">Conditions de paiement</label>
            <Input value={form.payment_conditions}
              onChange={e => setForm(p => ({ ...p, payment_conditions: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white" />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}
            className="border-slate-600 text-slate-300 hover:bg-slate-700">
            Annuler
          </Button>
          <Button onClick={handleSubmit}
            disabled={actionLoading.submit}
            data-testid="quote-submit-btn"
            className="bg-[#DFFF00] text-black hover:bg-[#c8e600] font-semibold">
            {actionLoading.submit ? 'Enregistrement...' : (editingQuote ? 'Mettre à jour' : 'Créer le devis')}
          </Button>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ────────────────────────────
  return (
    <div data-testid="quotes-view" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <ReceiptText className="w-6 h-6 text-[#DFFF00]" />
            Devis
          </h2>
          <p className="text-slate-400 text-sm mt-1">{quotes.length} devis au total</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }}
          data-testid="new-quote-btn"
          className="bg-[#DFFF00] text-black hover:bg-[#c8e600] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Nouveau devis
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Rechercher par numéro, client, pack..."
          data-testid="quote-search"
          className="pl-10 bg-slate-800/50 border-slate-700 text-white" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_MAP).map(([key, { label, color }]) => {
          const count = quotes.filter(q => q.status === key).length;
          return (
            <div key={key} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 text-center">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
              <div className="text-2xl font-bold text-white mt-1">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Quote List */}
      {loading ? (
        <div className="text-center py-10 text-slate-500">Chargement...</div>
      ) : filteredQuotes.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <ReceiptText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Aucun devis trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredQuotes.map(q => {
            const st = STATUS_MAP[q.status] || STATUS_MAP.draft;
            const isExpanded = expandedId === q.id;
            return (
              <div key={q.id} className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden" data-testid={`quote-row-${q.id}`}>
                {/* Row */}
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-700/30 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : q.id)}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-8 text-slate-500">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#DFFF00] text-sm">{q.quote_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                        {q.status === 'sent' && q.sent_at && (() => {
                          const days = Math.floor((Date.now() - new Date(q.sent_at).getTime()) / (1000 * 60 * 60 * 24));
                          return <span className="text-xs text-slate-500 ml-1">({days === 0 ? "aujourd'hui" : `${days}j`})</span>;
                        })()}
                      </div>
                      <div className="text-white text-sm mt-0.5 truncate">
                        {q.client?.company || `${q.client?.firstName || ''} ${q.client?.lastName || ''}`}
                        <span className="text-slate-500 ml-2">{q.client?.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 shrink-0">
                    {q.status === 'accepted' && !q.manual_order_id && (
                      <Button size="sm"
                        onClick={(e) => { e.stopPropagation(); handleConvert(q.id); }}
                        disabled={actionLoading[q.id + '_convert']}
                        data-testid={`quote-convert-row-${q.id}`}
                        className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 border border-purple-600/50">
                        <ArrowRight className="w-3 h-3 mr-1" />
                        {actionLoading[q.id + '_convert'] ? 'Conversion...' : 'Convertir en commande'}
                      </Button>
                    )}
                    <div className="text-right hidden md:block">
                      <div className="text-xs text-slate-500"><PackName name={q.pack_name} /></div>
                      <div className="text-white font-semibold">{q.total_ttc?.toFixed(2)}€ TTC/mois</div>
                    </div>
                    <div className="text-right hidden lg:block">
                      <div className="text-xs text-slate-500">Créé le</div>
                      <div className="text-slate-300 text-sm">{formatDate(q.created_at)}</div>
                    </div>
                    {q.sent_at && (
                      <div className="text-right hidden lg:block">
                        <div className="text-xs text-slate-500">Envoyé le</div>
                        <div className="text-slate-300 text-sm">{formatDate(q.sent_at)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50 p-4 bg-slate-900/30">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-slate-500 flex items-center gap-1"><Euro className="w-3 h-3" /> Mensuel TTC</div>
                        <div className="text-white font-semibold">{q.final_monthly_price?.toFixed(2)}€</div>
                        {q.discount_amount > 0 && (
                          <div className="text-red-400 text-xs">-{q.discount_amount?.toFixed(2)}€ remise</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 flex items-center gap-1"><Building className="w-3 h-3" /> Caution</div>
                        <div className="text-white font-semibold">{q.caution?.toFixed(2)}€</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Durée</div>
                        <div className="text-white font-semibold">{q.duration_months} mois (indicatif)</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Validité</div>
                        <div className="text-white font-semibold">Expire le {formatDate(q.expiry_date)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-slate-500">Transport</div>
                        <div className="text-white">{q.transport_fees > 0 ? `${q.transport_fees?.toFixed(2)}€` : 'Inclus'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Commercial</div>
                        <div className="text-white">{q.commercial_name || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Début souhaité</div>
                        <div className="text-white">{formatDate(q.start_date)}</div>
                      </div>
                      {q.manual_order_id && (
                        <div>
                          <div className="text-xs text-slate-500">Commande liée</div>
                          <div className="text-purple-400 text-sm font-mono">{q.manual_order_id.slice(0, 8)}...</div>
                        </div>
                      )}
                    </div>
                    {q.notes && (
                      <div className="mb-4 p-3 bg-slate-800 rounded-lg border border-slate-700">
                        <div className="text-xs text-slate-500 mb-1">Notes</div>
                        <div className="text-slate-300 text-sm whitespace-pre-line">{q.notes}</div>
                      </div>
                    )}
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-700/50">
                      <Button size="sm" variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleDownloadPdf(q.id, q.quote_number); }}
                        disabled={actionLoading[q.id + '_pdf']}
                        data-testid={`quote-download-pdf-${q.id}`}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700">
                        <Download className="w-3 h-3 mr-1" />
                        {actionLoading[q.id + '_pdf'] ? 'Génération...' : 'PDF'}
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleSendEmail(q.id); }}
                        disabled={actionLoading[q.id + '_email'] || !q.client?.email}
                        data-testid={`quote-send-email-${q.id}`}
                        className="border-blue-600 text-blue-400 hover:bg-blue-900/30">
                        <Mail className="w-3 h-3 mr-1" />
                        {actionLoading[q.id + '_email'] ? 'Envoi...' : 'Envoyer par email'}
                      </Button>
                      {q.status !== 'converted' && (
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleEdit(q); }}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          <Pencil className="w-3 h-3 mr-1" /> Modifier
                        </Button>
                      )}
                      {(q.status === 'sent' || q.status === 'draft') && (
                        <>
                          <Button size="sm"
                            onClick={(e) => { e.stopPropagation(); handleMarkAccepted(q.id); }}
                            disabled={actionLoading[q.id + '_accept']}
                            data-testid={`quote-accept-${q.id}`}
                            className="bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-600/50">
                            <CheckCircle className="w-3 h-3 mr-1" /> Accepter
                          </Button>
                          <Button size="sm"
                            onClick={(e) => { e.stopPropagation(); handleMarkRejected(q.id); }}
                            className="bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-600/50">
                            <XCircle className="w-3 h-3 mr-1" /> Refuser
                          </Button>
                        </>
                      )}
                      {q.status === 'accepted' && !q.manual_order_id && (
                        <Button size="sm"
                          onClick={(e) => { e.stopPropagation(); handleConvert(q.id); }}
                          disabled={actionLoading[q.id + '_convert']}
                          data-testid={`quote-convert-${q.id}`}
                          className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 border border-purple-600/50">
                          <ArrowRight className="w-3 h-3 mr-1" />
                          {actionLoading[q.id + '_convert'] ? 'Conversion...' : 'Convertir en commande'}
                        </Button>
                      )}
                      {q.status === 'draft' && (
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleDelete(q.id); }}
                          className="border-red-600/50 text-red-400 hover:bg-red-900/30 ml-auto">
                          <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, description: '', action: null })}
        onConfirm={() => deleteModal.action && deleteModal.action()}
        itemDescription={deleteModal.description}
      />
    </div>
  );
};

export default QuotesView;
